/**
 * API client.
 *
 * The dashboard talks only to /internal, authenticated by the session cookie
 * the login endpoint sets. Amounts arrive as strings (kobo) with a
 * pre-formatted display value alongside — the dashboard never parses money.
 */

/** One join-link sign-up's BVN as NIBSS knows it. The name is shown only to the admin who asked. */
export interface SignupInspection {
  bvnTail: string;
  status: 'found' | 'not_found' | 'invalid';
  name: string | null;
  phones: string[];
  matchesFirstPhone: boolean | null;
  matchesSecondPhone: boolean | null;
  nameVerdict: 'same' | 'likely' | 'different' | null;
}

/** The outcome of adding inDrive's names. Nothing is written when `errors` is not empty. */
export interface NameImport {
  named: number;
  already_named: number;
  not_found: string[];
  /** Drivers whose row had no name (empty or #N/A). */
  no_name: string[];
  errors: { row: number; column: string | null; code: string; message: string }[];
}

export interface ApiError { code: string; message: string; details?: unknown; correlation_id?: string }

export class RequestFailed extends Error {
  constructor(readonly status: number, readonly body: ApiError) {
    super(body.message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/internal${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body && !(init.headers as Record<string, string>)?.['content-type']
        ? { 'content-type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed = text ? safeJson(text) : null;

  if (!res.ok) {
    throw new RequestFailed(res.status, parsed?.error ?? { code: 'unknown', message: text || res.statusText });
  }
  return parsed as T;
}

function safeJson(text: string): any {
  try { return JSON.parse(text); } catch { return null; }
}

const get = <T,>(p: string) => request<T>(p);

/** Plain-text fetch, for the endpoints that return a file rather than JSON. */
async function getText(path: string): Promise<string> {
  const res = await fetch(`/internal${path}`, { credentials: 'same-origin' });
  const text = await res.text();
  if (!res.ok) {
    throw new RequestFailed(res.status, safeJson(text)?.error ?? { code: 'unknown', message: res.statusText });
  }
  return text;
}
const post = <T,>(p: string, body?: unknown) => request<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

// ── types ─────────────────────────────────────────────────────────────

export type Role = 'zeeh_admin' | 'indrive_admin' | 'indrive_ops' | 'reviewer';

export interface Me { user: { id: string; name: string; email: string; role: Role }; features: { manualAssist: boolean; autoDisburse: boolean }; adapters: string }

export interface Referral {
  referral_id: string;
  referring_driver_id: string | null;
  referred_driver_id: string | null;
  status: string;
  amount: string;
  amount_display: string;
  currency: string;
  payout_channel: 'bank' | 'wallet';
  bank_code: string | null;
  wallet_provider: string | null;
  destination_masked: string;
  reason_codes: string[];
  batch_id: string | null;
  source: string;
  external_ref: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string;
  /** For a referral still waiting on the driver: not_joined, phone_differs, bvn_not_confirmed, or unchecked. */
  waiting_reason?: 'not_joined' | 'phone_differs' | 'bvn_not_confirmed' | 'sent_back' | 'unchecked';
}

export interface ReasonExplained { code: string; label: string }

/** One approved payment, as the release screen lists it. */
export interface ReleaseRow {
  referral_id: string; driver_id: string | null; amount: string; amount_display: string;
  destination_masked: string; destination_label: string; created_at: string; reasons_explained: ReasonExplained[];
  indrive_name: string | null; nibss_name: string | null; name_verdict: 'same' | 'likely' | 'different' | null;
}

export interface Payout {
  payout_id: string; status: string; amount: string; amount_display: string;
  channel: string; destination_masked: string; provider: string | null;
  provider_reference: string | null; attempt_no: number; attempts: number;
  failure_code: string | null; created_at: string; completed_at: string | null;
  /** The provider's own wording for a refusal. Dashboard only. */
  failure_reason?: string | null;
}

export interface TimelineEntry {
  id: string; action: string; actor: string; actor_type: string;
  reason: string | null; before: unknown; after: unknown; at: string;
}

export interface ReferralDetail extends Referral {
  reasons_explained: ReasonExplained[];
  payouts: Payout[];
  checks: {
    kyc: null | {
      decision: string; reasons: ReasonExplained[]; aml_score: number | null; checked_at: string;
      bvn_resolved: boolean; nin_resolved: boolean; account_resolved: boolean;
      liveness: { is_live: boolean; match_score: number } | null;
      sanctions_hit: boolean | null; pep_hit: boolean | null; terrorist_hit: boolean | null;
    };
    fraud: null | {
      decision: string; score: number; reasons: ReasonExplained[];
      flags: { self_referral: boolean; device: boolean; velocity: boolean; account_reuse: boolean };
      checked_at: string;
    };
  };
  timeline: TimelineEntry[];
  webhooks: { sequenceNo: string; eventType: string; deliveryStatus: string; attempts: number; deliveredAt: string | null }[];
  driver_kyc: {
    awaiting_driver: boolean;
    link_issued_at: string | null;
    opened_at: string | null;
    consented_at: string | null;
    consent_version: string | null;
    identity_submitted_at: string | null;
    expires_at: string;
    /** The latest match failed only on the phone, so a Zeeh admin may accept it by hand. */
    can_accept_join_link: boolean;
  };
  /** The paid driver's name as inDrive's sheet has it. A claim, not verified. */
  indrive_name: string | null;
  referrer_risk: {
    verified_name: string | null;
    referrals_made: number;
    referrals_flagged: number;
    total_earned_display: string | null;
    shares_identity_with: number;
    shares_account_with: number;
    shares_device_with: number;
    links: { driver_ref: string; driver_id: string; verified_name: string | null; kind: string; severity: 'hard' | 'soft' }[];
  };
}

export interface FloatAccount {
  id?: string; programme: string; currency: string;
  balance: string; balance_display: string;
  active_holds: string; active_holds_display: string;
  available: string; available_display: string;
  low_balance_threshold: string; frozen: boolean; frozen_reason: string | null;
  estimated_runway?: { payouts: number | null };
}

export interface Overview {
  kpis: {
    processed: number; paid: number; paid_amount: string; paid_amount_display: string;
    pending_kyc: number; awaiting_payout: number; flagged: number; rejected: number;
  };
  by_status: Record<string, number>;
  float: FloatAccount | null;
  all_float_accounts: FloatAccount[];
  queue: { pending: number; running: number; dead: number };
  webhooks: { pending: number; delivering: number; delivered: number; dead: number };
  trend: { day: string; received: number; paid: number }[];
}

export interface LedgerEntry {
  id: string; type: string; amount: string; amount_display: string;
  balance_after: string; balance_after_display: string; held_after: string;
  reference: string | null; narrative: string; referral_id: string | null; created_at: string;
}

export interface Driver {
  driver_ref: string;
  driver_id: string;
  verified_name: string | null;
  kyc_status: 'unverified' | 'pending' | 'in_progress' | 'verified' | 'failed';
  phone_masked: string | null;
  self_phone_masked: string | null;
  bvn_masked: string | null;
  nin_masked: string | null;
  referrals_made: number;
  referrals_paid: number;
  referrals_flagged: number;
  referrals_rejected: number;
  total_earned: string;
  total_earned_display: string;
  pending_value: string;
  pending_value_display: string;
  shared_account_with: number;
  first_seen: string;
  last_activity: string;
}

export interface NameAgreement { score: number; weakest: number; matched: boolean; borderline: boolean }

export interface KycEvidence {
  decision: string;
  checked_at: string;
  reason_codes: string[];
  sources: {
    bvn: null | { status: string; name: string | null; date_of_birth: string | null; provider_reference: string | null; has_photo: boolean };
    nin: null | { status: string; name: string | null; date_of_birth: string | null; provider_reference: string | null };
    bank_account: null | { status: string; account_name: string | null; bank_name: string | null; provider_reference: string | null };
    biometrics: null | { status: string; is_live: boolean | null; match_score: number | null };
  };
  agreement: { bvn_vs_nin: NameAgreement | null; identity_vs_account: NameAgreement | null };
  screening: { sanctions_hit: boolean | null; terrorist_hit: boolean | null; pep_hit: boolean | null; matched_lists: string[]; aml_score: number | null };
}

export interface DriverDetail extends Driver {
  recruited_by: null | {
    referral_id: string; status: string; amount_display: string;
    driver_id: string; driver_ref: string; verified_name: string | null; at: string;
  };

  kyc_evidence: KycEvidence | null;
}

export interface DriverReferral {
  referral_id: string; status: string; amount: string; amount_display: string;
  payout_channel: string; destination_masked: string; reason_codes: string[];
  created_at: string; referred_driver_id: string; referred_name: string | null;
  referred_kyc_status: string;
}

export interface DriverLink {
  driver_ref: string; driver_id: string; verified_name: string | null;
  kind: 'payout_account' | 'device' | 'phone' | 'bvn';
  occurrences: number; severity: 'hard' | 'soft';
}

export interface Batch {
  batch_id: string; client_id?: string; source_file: string; status: string;
  total_rows: number; processed: number; passed: number; failed: number;
  errors?: { row: number; column: string | null; code: string; message: string }[];
  created_at: string;
}

// ── endpoints ─────────────────────────────────────────────────────────

/** A driver who signed up through the join link. Nothing about it has been checked. */
export interface Enrolment {
  id: string;
  driver_id: string | null;
  phone: string;
  bvn_masked: string;
  nin_supplied: boolean;
  paid_to: string;
  account_masked: string;
  joined_at: string;
}

export const api = {
  login: (email: string, password: string) => post<{ token: string; user: Me['user'] }>('/auth/login', { email, password }),
  logout: () => post<{ ok: true }>('/auth/logout'),
  me: () => get<Me>('/auth/me'),

  overview: () => get<Overview>('/overview'),

  referrals: (q: Record<string, string | undefined>) =>
    get<{ referrals: Referral[]; next_cursor: string | null }>(`/referrals?${toQuery(q)}`),
  /** How many referrals are in each status, and why the waiting ones wait. */
  referralCounts: (batchId?: string) =>
    get<{ by_status: Record<string, number>; waiting: Record<string, number> }>(`/referrals/counts?${toQuery({ batch_id: batchId })}`),
  /**
   * Raise one referral by hand — for proving a flow works or keying in
   * something that came in outside the API. Same validation as inDrive's own
   * integration; `client_id` still has to name a real registered client.
   */
  createReferral: (body: {
    client_id: string;
    referring_driver_id: string;
    referred_driver_id: string;
    phone: string;
    commission_amount: string;
    payout_channel?: 'bank' | 'wallet';
    bank_code?: string;
    account_number?: string;
    wallet_provider?: 'opay' | 'kuda' | 'palmpay' | 'moniepoint';
    wallet_id?: string;
    referring_bvn?: string;
    referring_nin?: string;
    external_ref?: string;
  }) => post<Referral & { kyc_url?: string }>('/referrals', body),
  referral: (id: string) => get<ReferralDetail>(`/referrals/${id}`),
  bulkApprove: (b: { referral_ids: string[]; reason: string; expected_count: number; expected_total: string }) =>
    post<{ approved: number; total: string; failed: { referral_id: string; message: string }[] }>('/queue/flagged/bulk-approve', b),
  releasable: () =>
    get<{
      count: number; more: boolean; total: string; total_display: string; referral_ids: string[];
      items: ReleaseRow[]; held: ReleaseRow[]; held_count: number;
      float_available_display: string | null; shortfall: string; shortfall_display: string | null;
    }>('/referrals/releasable'),
  bulkRelease: (b: { referral_ids: string[]; expected_count: number; expected_total: string }) =>
    post<{ queued: number; total: string; alreadyQueued: number }>('/referrals/bulk-release', b),
  linkSignup: (signupId: string, driverId: string, reason: string) =>
    post<{ message: string }>(`/enrolments/${signupId}/link-to-referral`, { driver_id: driverId, reason }),
  bulkAcceptEnrolments: (referralIds: string[], reason: string) =>
    post<{ items: { referral_id: string; ok: boolean; message: string }[] }>('/referrals/bulk-accept-enrolment', { referral_ids: referralIds, reason }),
  bulkRecheckEnrolments: (referralIds: string[]) =>
    post<{ items: { referral_id: string; ok: boolean; matched: boolean; message: string }[] }>('/referrals/bulk-recheck-enrolment', { referral_ids: referralIds }),
  recheckEnrolment: (id: string) =>
    post<{ matched: boolean; message: string; canOverride: boolean }>(`/referrals/${id}/recheck-enrolment`),
  acceptEnrolment: (id: string, reason: string) =>
    post<{ message: string }>(`/referrals/${id}/accept-enrolment`, { reason }),
  inspectSignups: (id: string, reason: string, indriveName: string) =>
    post<{ signups: SignupInspection[] }>(`/referrals/${id}/inspect-signups`, { reason, indrive_name: indriveName || undefined }),
  importIndriveNames: (file: { file_name: string; csv?: string; xlsx_base64?: string }) =>
    post<NameImport>('/drivers/indrive-names', file),
  cancelReferral: (id: string, reason: string) =>
    post<{ status: string }>(`/referrals/${id}/cancel`, { reason }),
  kycLink: (id: string) =>
    post<{ kyc_url: string; expires_at: string; opened: boolean; consented: boolean }>(`/referrals/${id}/kyc-link`),
  releasePayout: (id: string) => post<{ status: string; payoutId?: string; reasonCodes: string[] }>(`/referrals/${id}/release-payout`),

  flagged: () => get<{ referrals: (Referral & { reasons_explained: ReasonExplained[]; fraud: { score: number; reasons: ReasonExplained[] } | null; bulk_approvable?: boolean; phone_accepted?: boolean; indrive_name?: string | null; nibss_name?: string | null; name_verdict?: 'same' | 'likely' | 'different' | null })[] }>('/queue/flagged'),
  review: (id: string, decision: 'approve' | 'reject', reason: string) =>
    post<Referral>(`/queue/flagged/${id}/review`, { decision, reason }),

  float: (accountId?: string) =>
    get<{ accounts: FloatAccount[]; selected: string; ledger: LedgerEntry[] }>(`/float${accountId ? `?account_id=${accountId}` : ''}`),
  fund: (accountId: string, amount: string, reference: string) =>
    post<FloatAccount>(`/float/${accountId}/fund`, { amount, reference }),
  setThreshold: (accountId: string, threshold: string) => post<{ ok: true }>(`/float/${accountId}/threshold`, { threshold }),
  unfreeze: (accountId: string, reason: string) => post<{ ok: true }>(`/float/${accountId}/unfreeze`, { reason }),

  batches: () => get<{ batches: Batch[] }>('/batches'),
  csvTemplate: () => getText('/batches/template.csv'),
  enrolmentsCsv: () => getText('/enrolments/export.csv'),
  batch: (id: string) => get<Batch>(`/batches/${id}`),
  upload: (clientId: string, fileName: string, csv: string) =>
    request<{ batch_id: string; accepted: boolean; total_rows: number; errors: Batch['errors'] }>(
      `/batches/upload?client_id=${encodeURIComponent(clientId)}&file_name=${encodeURIComponent(fileName)}`,
      { method: 'POST', headers: { 'content-type': 'text/csv' }, body: csv },
    ),

  drivers: (q: Record<string, string | undefined>) => get<{ drivers: Driver[] }>(`/drivers?${toQuery(q)}`),
  enrolments: (q: Record<string, string | undefined>) =>
    get<{ enrolments: Enrolment[]; total_signups: number; total_phones: number }>(`/enrolments?${toQuery(q)}`),
  driver: (id: string) =>
    get<Driver & {
      recruited_by: DriverDetail['recruited_by'];
      recruited: DriverReferral[];
      linked_drivers: DriverLink[];
      kyc_evidence: KycEvidence | null;
    }>(`/drivers/${encodeURIComponent(id)}`),
  reveal: (id: string, field: 'bvn' | 'nin' | 'phone', reason: string) =>
    post<{ field: string; value: string; revealed_by: string; revealed_at: string }>(
      `/drivers/${encodeURIComponent(id)}/reveal`, { field, reason },
    ),
  leaderboard: () => get<{ by_earned: Driver[]; by_volume: Driver[] }>('/leaderboard'),

  deadLetters: () => get<{ jobs: any[]; webhooks: any[] }>('/ops/dead-letters'),
  requeueJob: (id: string) => post<{ ok: true }>(`/ops/jobs/${id}/requeue`),
  replayWebhook: (id: string) => post<{ ok: true }>(`/ops/webhooks/${id}/replay`),
  reconciliation: () => get<{ runs: any[] }>('/ops/reconciliation'),

  exportUrl: (q: Record<string, string | undefined>) => `/internal/export/referrals.csv?${toQuery(q)}`,
};

function toQuery(q: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) p.set(k, v);
  return p.toString();
}
